import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Sparkles, ExternalLink, ShoppingBag, X, Plus, Save, RefreshCw, Pencil, Image as ImageIcon } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminRecipes() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genCategory, setGenCategory] = useState("");
  const [genProvider, setGenProvider] = useState("openai");
  const [genTheme, setGenTheme] = useState("");
  const [affEditing, setAffEditing] = useState(null); // {recipe, products: []}
  const [affSaving, setAffSaving] = useState(false);
  const [regenJob, setRegenJob] = useState(null); // active regen job
  const [singleRegen, setSingleRegen] = useState({}); // recipe id -> bool
  const [selectedIds, setSelectedIds] = useState(new Set()); // selected recipe ids for bulk regen
  const [filterWithoutAi, setFilterWithoutAi] = useState(false);
  const [withoutAiIds, setWithoutAiIds] = useState(new Set()); // recipe IDs without AI image
  const [withoutAiTotal, setWithoutAiTotal] = useState(0);
  const [batchSize, setBatchSize] = useState(10);

  // Filters, sort, pagination
  const [filterCategory, setFilterCategory] = useState("");
  const [filterCountry, setFilterCountry] = useState("");
  const [filterGiLevel, setFilterGiLevel] = useState("");
  const [filterSource, setFilterSource] = useState("");        // "", "ia", "manuel"
  const [filterPublished, setFilterPublished] = useState("");  // "", "true", "false"
  const [filterHasAiImage, setFilterHasAiImage] = useState(""); // "", "true", "false"
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [filterOptions, setFilterOptions] = useState({ categories: [], countries: [] });

  const COST_PER_IMAGE_USD = 0.039;
  const formatCost = (n) => {
    const usd = (n * COST_PER_IMAGE_USD).toFixed(2);
    const eur = (n * COST_PER_IMAGE_USD * 0.92).toFixed(2);
    return `~${usd} $ (~${eur} €)`;
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === items.length) return new Set();
      return new Set(items.map((r) => r.id));
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Poll the active job every 3 seconds
  useEffect(() => {
    let mounted = true;
    let timer = null;

    const tick = async () => {
      try {
        const r = await api.get("/admin/regen-jobs/latest");
        if (!mounted) return;
        const job = r.data;
        if (!job || job.status === "none") {
          setRegenJob(null);
          return;
        }
        setRegenJob(job);
        if (job.status === "queued" || job.status === "running") {
          timer = setTimeout(tick, 3000);
        } else if (job.status === "done") {
          // Refresh recipes and without-ai count once when job completes
          load();
          loadWithoutAi();
        }
      } catch (e) {
        // Silent — endpoint may not exist yet on older builds
      }
    };

    tick();
    return () => {
      mounted = false;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line
  }, []);

  const cancelRegenJob = async () => {
    if (!regenJob?.id) return;
    if (!window.confirm("Annuler / réinitialiser ce job ? (Si zombie, ça le débloquera)")) return;
    try {
      await api.post(`/admin/regen-jobs/${regenJob.id}/cancel`);
      toast.success("Job annulé");
      // Force refresh
      const r = await api.get("/admin/regen-jobs/latest");
      setRegenJob(r.data.status === "none" ? null : r.data);
    } catch (e) {
      toast.error("Erreur d'annulation");
    }
  };

  const cleanTitles = async () => {
    if (!window.confirm("Retirer '(recette modèle)' de tous les titres de recettes ?")) return;
    try {
      const r = await api.post("/admin/recipes/clean-titles");
      const d = r.data;
      if (d.cleaned_count > 0) {
        toast.success(`${d.cleaned_count} titre(s) nettoyé(s) sur ${d.total}`);
        load();
      } else {
        toast.info(`Aucun titre à nettoyer (${d.total} recettes analysées)`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Erreur : ${detail || "inconnue"}`);
    }
  };

  const regenerateAllImages = async () => {
    if (regenJob && (regenJob.status === "queued" || regenJob.status === "running")) {
      toast.info("Un job est déjà en cours.");
      return;
    }
    if (!window.confirm(`Régénérer les images de TOUTES les recettes via Nano Banana ?\n\nCoût estimé : ${formatCost(Math.min(300, total))}\n(Tâche en arrière-plan, ~10s par recette.)`)) return;
    try {
      const r = await api.post("/admin/recipes/regenerate-images?limit=300");
      const d = r.data;
      if (d.already_running) {
        toast.info("Un job est déjà en cours");
      } else {
        toast.success("Régénération lancée en arrière-plan");
      }
      api.get("/admin/regen-jobs/latest").then((rr) => setRegenJob(rr.data));
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Erreur de lancement: ${detail || "inconnue"}`);
    }
  };

  const regenerateSelected = async () => {
    if (selectedIds.size === 0) {
      toast.info("Aucune recette sélectionnée");
      return;
    }
    if (regenJob && (regenJob.status === "queued" || regenJob.status === "running")) {
      toast.info("Un job est déjà en cours.");
      return;
    }
    if (!window.confirm(`Régénérer ${selectedIds.size} recette(s) sélectionnée(s) ?\n\nCoût estimé : ${formatCost(selectedIds.size)}\n(~10s par recette, en arrière-plan.)`)) return;
    try {
      const r = await api.post("/admin/recipes/regenerate-images", {
        recipe_ids: Array.from(selectedIds),
      });
      const d = r.data;
      if (d.already_running) {
        toast.info("Un job est déjà en cours");
      } else {
        toast.success(`Régénération lancée pour ${selectedIds.size} recette(s)`);
        clearSelection();
      }
      api.get("/admin/regen-jobs/latest").then((rr) => setRegenJob(rr.data));
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Erreur de lancement: ${detail || "inconnue"}`);
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`⚠️ SUPPRIMER DÉFINITIVEMENT ${selectedIds.size} recette(s) ?\nCette action est irréversible.`)) return;
    try {
      const r = await api.post("/admin/recipes/bulk-delete", {
        recipe_ids: Array.from(selectedIds),
      });
      toast.success(`${r.data.deleted_count} recette(s) supprimée(s)`);
      clearSelection();
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Erreur suppression: ${detail || "inconnue"}`);
    }
  };

  const applyDefaultImage = async () => {
    if (!window.confirm(
      "Appliquer l'image par défaut à toutes les recettes SANS image Nano Banana ?\n" +
      "(Ne touche pas aux recettes ayant déjà une image générée par l'IA — préserve tout votre travail Nano Banana.)"
    )) return;
    try {
      const r = await api.post("/admin/recipes/apply-default-image", {
        image_url: "/recipe-default.jpg",
      });
      toast.success(`${r.data.modified} recette(s) mise(s) à jour avec l'image par défaut`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Erreur: ${detail || "inconnue"}`);
    }
  };

  const regenerateOne = async (recipeId, title) => {
    if (!window.confirm(`Régénérer l'image de "${title}" ?`)) return;
    setSingleRegen((s) => ({ ...s, [recipeId]: true }));
    try {
      const r = await api.post(`/admin/recipes/${recipeId}/regenerate-image`);
      if (r.data.success) {
        toast.success(`Image régénérée (${(r.data.duration_ms / 1000).toFixed(1)}s)`);
        load();
      } else {
        toast.error(`Échec: ${r.data.error}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Erreur: ${detail || "inconnue"}`);
    } finally {
      setSingleRegen((s) => ({ ...s, [recipeId]: false }));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(pageSize),
        skip: String((page - 1) * pageSize),
        sort_field: sortField,
        sort_order: sortOrder,
      });
      if (q) params.set("q", q);
      if (filterCategory) params.set("category", filterCategory);
      if (filterCountry) params.set("country", filterCountry);
      if (filterGiLevel) params.set("gi_level", filterGiLevel);
      if (filterSource) params.set("source", filterSource);
      if (filterPublished) params.set("published", filterPublished);
      // Use the toggle OR the dropdown (toggle takes priority when both set)
      const effectiveAiFilter = filterWithoutAi ? "false" : filterHasAiImage;
      if (effectiveAiFilter) params.set("has_ai_image", effectiveAiFilter);
      const r = await api.get(`/admin/recipes?${params}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || e?.message || "Erreur inconnue";
      toast.error(`Chargement des recettes échoué (${status || "réseau"}): ${msg}`);
      console.error("[AdminRecipes] load failed:", e);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const loadWithoutAi = async () => {
    try {
      const r = await api.get("/admin/recipes/without-ai-image?limit=200");
      const items = r.data?.items || [];
      setWithoutAiIds(new Set(items.map((it) => it.id)));
      setWithoutAiTotal(r.data?.total || 0);
    } catch {
      // silent — non-critical
    }
  };

  const loadFilterOptions = async () => {
    try {
      const r = await api.get("/admin/recipes/filter-options");
      setFilterOptions(r.data || { categories: [], countries: [] });
    } catch {
      // ignore
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { loadWithoutAi(); loadFilterOptions(); }, []);
  useEffect(() => {
    // Debounce search + reload on any filter/sort/page change
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, filterCategory, filterCountry, filterGiLevel, filterSource, filterPublished, filterHasAiImage, filterWithoutAi, sortField, sortOrder, page, pageSize]);

  // Reset to page 1 when filters/sort change (but not when page itself changes)
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line
  }, [q, filterCategory, filterCountry, filterGiLevel, filterSource, filterPublished, filterHasAiImage, filterWithoutAi, sortField, sortOrder, pageSize]);

  const resetFilters = () => {
    setQ("");
    setFilterCategory("");
    setFilterCountry("");
    setFilterGiLevel("");
    setFilterSource("");
    setFilterPublished("");
    setFilterHasAiImage("");
    setFilterWithoutAi(false);
    setSortField("created_at");
    setSortOrder("desc");
    setPage(1);
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const activeFiltersCount = [
    q, filterCategory, filterCountry, filterGiLevel, filterSource, filterPublished,
    filterHasAiImage || (filterWithoutAi ? "false" : ""),
  ].filter(Boolean).length;

  const selectNoAiBatch = async () => {
    const n = Math.max(1, Math.min(50, parseInt(batchSize, 10) || 10));
    try {
      const r = await api.get(`/admin/recipes/without-ai-image?limit=${n}`);
      const ids = (r.data?.items || []).map((it) => it.id);
      if (ids.length === 0) {
        toast.info("Toutes les recettes ont déjà une image IA");
        return;
      }
      setSelectedIds(new Set(ids));
      // Turn on the filter so the user sees exactly which ones are selected
      setFilterWithoutAi(true);
      toast.success(`${ids.length} recette(s) sans image IA sélectionnée(s)`);
    } catch (e) {
      toast.error("Impossible de charger la liste");
    }
  };

  const runNoAiBatch = async () => {
    if (regenJob && (regenJob.status === "queued" || regenJob.status === "running")) {
      toast.info("Un job est déjà en cours.");
      return;
    }
    const n = Math.max(1, Math.min(50, parseInt(batchSize, 10) || 10));
    try {
      const listResp = await api.get(`/admin/recipes/without-ai-image?limit=${n}`);
      const ids = (listResp.data?.items || []).map((it) => it.id);
      if (ids.length === 0) {
        toast.info("Toutes les recettes ont déjà une image IA");
        return;
      }
      if (!window.confirm(
        `Régénérer ${ids.length} image(s) via Nano Banana ?\n\nCoût estimé : ${formatCost(ids.length)}\n(~10s par image, en arrière-plan.)`
      )) return;

      const r = await api.post("/admin/recipes/regenerate-images", { recipe_ids: ids });
      if (r.data.already_running) {
        toast.info("Un job est déjà en cours");
      } else {
        toast.success(`Batch de ${ids.length} lancé`);
      }
      api.get("/admin/regen-jobs/latest").then((rr) => setRegenJob(rr.data));
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Erreur: ${detail || "inconnue"}`);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette recette ?")) return;
    try {
      await api.delete(`/admin/recipes/${id}`);
      toast.success("Recette supprimée");
      load();
    } catch (e) {
      toast.error("Erreur de suppression");
    }
  };

  const togglePublish = async (r) => {
    try {
      await api.patch(`/admin/recipes/${r.id}`, { published: !r.published });
      toast.success(r.published ? "Recette dépubliée" : "Recette publiée");
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const generate = async () => {
    setGenerating(true);
    toast.info("Génération en cours…");
    try {
      const r = await api.post("/admin/recipes/generate", {
        category: genCategory || undefined,
        provider: genProvider,
        theme: genTheme || undefined,
        generate_image: true,
        count: 1,
      });
      if (r.data.created?.length) {
        toast.success(`Recette créée : ${r.data.created[0].title}`);
      }
      if (r.data.errors?.length) {
        toast.error(r.data.errors[0]);
      }
      setGenTheme("");
      load();
    } catch (e) {
      toast.error("Erreur génération");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="admin-recipes-page">
      <div className="flex items-baseline justify-between mb-6 gap-4">
        <div>
          <p className="overline mb-2">{total} recette{total > 1 ? "s" : ""}{activeFiltersCount > 0 ? ` (${activeFiltersCount} filtre${activeFiltersCount > 1 ? "s" : ""})` : ""}</p>
          <h1 className="font-serif text-4xl">Recettes</h1>
        </div>
        <input
          data-testid="admin-recipes-search"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher…"
          className="text-sm bg-white border border-[hsl(var(--border-light))] rounded-sm px-3 py-2 focus:outline-none focus:border-[hsl(var(--brand-primary))]"
        />
      </div>

      {/* FILTER PANEL */}
      <div className="bg-white border border-[hsl(var(--border-light))] p-4 rounded-sm mb-6" data-testid="filter-panel">
        <div className="flex items-center justify-between mb-3">
          <p className="overline text-xs">Filtres &amp; tri</p>
          {activeFiltersCount > 0 && (
            <button
              onClick={resetFilters}
              data-testid="reset-filters-btn"
              className="text-xs text-[hsl(var(--brand-primary))] hover:underline flex items-center gap-1"
            >
              <X className="w-3.5 h-3.5" strokeWidth={1.5} /> Réinitialiser
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-[hsl(var(--text-muted))]">Catégorie</span>
            <select
              data-testid="filter-category"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="bg-white border border-[hsl(var(--border-light))] rounded-sm px-2 py-1.5 focus:outline-none focus:border-[hsl(var(--brand-primary))]"
            >
              <option value="">Toutes</option>
              {filterOptions.categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-[hsl(var(--text-muted))]">Pays</span>
            <select
              data-testid="filter-country"
              value={filterCountry}
              onChange={(e) => setFilterCountry(e.target.value)}
              className="bg-white border border-[hsl(var(--border-light))] rounded-sm px-2 py-1.5 focus:outline-none focus:border-[hsl(var(--brand-primary))]"
            >
              <option value="">Tous</option>
              {filterOptions.countries.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-[hsl(var(--text-muted))]">Niveau IG</span>
            <select
              data-testid="filter-gi"
              value={filterGiLevel}
              onChange={(e) => setFilterGiLevel(e.target.value)}
              className="bg-white border border-[hsl(var(--border-light))] rounded-sm px-2 py-1.5 focus:outline-none focus:border-[hsl(var(--brand-primary))]"
            >
              <option value="">Tous</option>
              <option value="bas">IG bas</option>
              <option value="modere">IG modéré</option>
              <option value="eleve">IG élevé</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-[hsl(var(--text-muted))]">Source</span>
            <select
              data-testid="filter-source"
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="bg-white border border-[hsl(var(--border-light))] rounded-sm px-2 py-1.5 focus:outline-none focus:border-[hsl(var(--brand-primary))]"
            >
              <option value="">Toutes</option>
              <option value="ia">Générée IA</option>
              <option value="manuel">Manuelle</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-[hsl(var(--text-muted))]">Statut</span>
            <select
              data-testid="filter-published"
              value={filterPublished}
              onChange={(e) => setFilterPublished(e.target.value)}
              className="bg-white border border-[hsl(var(--border-light))] rounded-sm px-2 py-1.5 focus:outline-none focus:border-[hsl(var(--brand-primary))]"
            >
              <option value="">Tous</option>
              <option value="true">Publiées</option>
              <option value="false">Brouillons</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-[hsl(var(--text-muted))]">Image IA</span>
            <select
              data-testid="filter-image"
              value={filterHasAiImage}
              onChange={(e) => { setFilterHasAiImage(e.target.value); setFilterWithoutAi(false); }}
              className="bg-white border border-[hsl(var(--border-light))] rounded-sm px-2 py-1.5 focus:outline-none focus:border-[hsl(var(--brand-primary))]"
            >
              <option value="">Toutes</option>
              <option value="true">Avec image IA</option>
              <option value="false">Sans image IA</option>
            </select>
          </label>
        </div>
      </div>

      {/* REGENERATION JOB STATUS */}
      {regenJob && regenJob.status !== "none" && (
        <div
          className="bg-white border border-[hsl(var(--border-light))] p-5 rounded-sm mb-6"
          data-testid="regen-job-status"
        >
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-3">
            <p className="overline">
              {regenJob.status === "queued" && "Génération en file d'attente…"}
              {regenJob.status === "running" && "Génération d'images en cours"}
              {regenJob.status === "done" && "Régénération terminée"}
              {regenJob.status === "failed" && "Régénération en échec"}
              {regenJob.status === "cancelled" && "Régénération annulée"}
            </p>
            <div className="flex items-center gap-4">
              <p className="text-xs text-[hsl(var(--text-muted))] font-mono">
                {regenJob.processed}/{regenJob.total} · ✓ {regenJob.success_count} · ✗ {regenJob.error_count}
              </p>
              {(regenJob.status === "queued" || regenJob.status === "running") && (
                <button
                  onClick={cancelRegenJob}
                  data-testid="regen-cancel-btn"
                  className="text-xs text-[hsl(var(--brand-primary))] hover:underline"
                >
                  Annuler
                </button>
              )}
              {(regenJob.status === "failed" || regenJob.status === "cancelled" || regenJob.status === "done") && (
                <button
                  onClick={() => setRegenJob(null)}
                  data-testid="regen-dismiss-btn"
                  className="text-xs text-[hsl(var(--text-muted))] hover:underline"
                >
                  Masquer
                </button>
              )}
            </div>
          </div>
          {regenJob.total > 0 && (
            <div className="w-full h-2 bg-[hsl(var(--bg-subtle))] rounded-sm overflow-hidden mb-2">
              <div
                className="h-full bg-[hsl(var(--brand-primary))] transition-all duration-500"
                style={{ width: `${Math.min(100, (regenJob.processed / regenJob.total) * 100)}%` }}
                data-testid="regen-progress-bar"
              />
            </div>
          )}
          {regenJob.current && (
            <p className="text-xs text-[hsl(var(--text-secondary))] truncate" data-testid="regen-current">
              <strong>En cours :</strong> {regenJob.current}
            </p>
          )}
          {regenJob.fatal_error && (
            <p className="text-xs text-[hsl(var(--brand-primary))] mt-2 font-mono" data-testid="regen-fatal">
              {regenJob.fatal_error}
            </p>
          )}
          {regenJob.status === "done" && regenJob.errors?.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs text-[hsl(var(--brand-primary))] cursor-pointer">
                {regenJob.errors.length} erreur(s) — voir détails
              </summary>
              <ul className="mt-2 space-y-1 text-xs">
                {regenJob.errors.slice(0, 20).map((er, i) => (
                  <li key={i} className="text-[hsl(var(--text-muted))]">
                    <span className="font-medium">{er.title}</span> — {er.error}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* GENERATION PANEL */}
      <div className="bg-white border border-[hsl(var(--border-light))] p-5 rounded-sm mb-8" data-testid="admin-generate-recipe-panel">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-3">
          <p className="overline">Générer une recette IA</p>
          <div className="flex items-center gap-4 text-xs">
            <button
              onClick={cleanTitles}
              data-testid="clean-titles-btn"
              className="text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--brand-primary))] hover:underline flex items-center gap-1"
              title="Retire '(recette modèle)' des titres"
            >
              <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
              Nettoyer les titres
            </button>
            <button
              onClick={applyDefaultImage}
              data-testid="apply-default-image-btn"
              className="text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--brand-primary))] hover:underline flex items-center gap-1"
              title="Applique l'image générique à toutes les recettes sans image Nano Banana"
            >
              <ImageIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
              Image par défaut aux recettes sans IA
            </button>
            <button
              onClick={regenerateAllImages}
              disabled={regenJob && (regenJob.status === "queued" || regenJob.status === "running")}
              data-testid="regenerate-images-btn"
              className="text-[hsl(var(--brand-primary))] hover:underline disabled:opacity-50"
            >
              {regenJob && (regenJob.status === "queued" || regenJob.status === "running")
                ? "Régénération en cours…"
                : "Régénérer toutes les images Nano Banana"}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select value={genCategory} onChange={(e) => setGenCategory(e.target.value)} data-testid="gen-category-select"
            className="text-sm bg-white border border-[hsl(var(--border-light))] rounded-sm px-3 py-2 focus:outline-none focus:border-[hsl(var(--brand-primary))]">
            <option value="">Aléatoire</option>
            <option value="entree">Entrée</option>
            <option value="plat">Plat</option>
            <option value="dessert">Dessert</option>
            <option value="petit-dejeuner">Petit-déjeuner</option>
            <option value="accompagnement">Accompagnement</option>
          </select>
          <select value={genProvider} onChange={(e) => setGenProvider(e.target.value)} data-testid="gen-provider-select"
            className="text-sm bg-white border border-[hsl(var(--border-light))] rounded-sm px-3 py-2 focus:outline-none focus:border-[hsl(var(--brand-primary))]">
            <option value="openai">GPT-5.2 (OpenAI)</option>
            <option value="gemini">Gemini 3 Pro</option>
          </select>
          <input
            type="text"
            value={genTheme}
            onChange={(e) => setGenTheme(e.target.value)}
            placeholder="Thème (optionnel) — ex: blanquette de veau"
            data-testid="gen-theme-input"
            className="text-sm bg-white border border-[hsl(var(--border-light))] rounded-sm px-3 py-2 focus:outline-none focus:border-[hsl(var(--brand-primary))]"
          />
          <button onClick={generate} disabled={generating} data-testid="gen-recipe-btn" className="btn-primary disabled:opacity-50">
            <Sparkles className="w-4 h-4" strokeWidth={1.5} />
            {generating ? "…" : "Générer"}
          </button>
        </div>
      </div>

      {/* BATCH IMAGE REGENERATION BAR */}
      <div
        className="bg-white border border-[hsl(var(--border-light))] p-4 rounded-sm mb-6 flex flex-wrap items-center justify-between gap-4"
        data-testid="batch-regen-bar"
      >
        <div className="flex items-center gap-3 text-sm">
          <span className="overline">Batch d'images</span>
          <span className="text-[hsl(var(--text-secondary))]">
            <strong className="text-[hsl(var(--brand-primary))]">{withoutAiTotal}</strong>{" "}
            recette{withoutAiTotal > 1 ? "s" : ""} sans image IA
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-xs text-[hsl(var(--text-secondary))] flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filterWithoutAi}
              onChange={(e) => setFilterWithoutAi(e.target.checked)}
              data-testid="filter-without-ai-toggle"
              className="accent-[hsl(var(--brand-primary))]"
            />
            Afficher uniquement sans image IA
          </label>
          <div className="flex items-center gap-2">
            <label className="text-xs text-[hsl(var(--text-muted))]">Taille du batch</label>
            <input
              type="number"
              min={1}
              max={50}
              value={batchSize}
              onChange={(e) => setBatchSize(e.target.value)}
              data-testid="batch-size-input"
              className="text-sm w-16 bg-white border border-[hsl(var(--border-light))] rounded-sm px-2 py-1 focus:outline-none focus:border-[hsl(var(--brand-primary))]"
            />
          </div>
          <button
            onClick={selectNoAiBatch}
            data-testid="select-batch-btn"
            disabled={withoutAiTotal === 0}
            className="text-sm px-3 py-1.5 border border-[hsl(var(--border-light))] rounded-sm hover:border-[hsl(var(--brand-primary))] hover:text-[hsl(var(--brand-primary))] disabled:opacity-40 flex items-center gap-1"
            title="Coche les N recettes sans image IA dans la liste ci-dessous"
          >
            Sélectionner {batchSize} sans image
          </button>
          <button
            onClick={runNoAiBatch}
            data-testid="run-batch-btn"
            disabled={(regenJob && (regenJob.status === "queued" || regenJob.status === "running")) || withoutAiTotal === 0}
            className="text-sm px-3 py-1.5 bg-[hsl(var(--brand-primary))] text-white rounded-sm hover:opacity-90 disabled:opacity-40 flex items-center gap-1"
            title={`Coût estimé: ${formatCost(Math.min(batchSize, withoutAiTotal))}`}
          >
            <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />
            Lancer batch de {batchSize} — {formatCost(Math.min(batchSize, withoutAiTotal))}
          </button>
        </div>
      </div>

      {/* BULK SELECTION BAR */}
      {selectedIds.size > 0 && (
        <div
          className="bg-[hsl(var(--brand-primary))] text-white rounded-sm p-4 mb-4 flex items-center justify-between flex-wrap gap-3"
          data-testid="bulk-action-bar"
        >
          <p className="text-sm">
            <strong>{selectedIds.size}</strong> recette(s) sélectionnée(s)
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={regenerateSelected}
              disabled={regenJob && (regenJob.status === "queued" || regenJob.status === "running")}
              data-testid="bulk-regen-btn"
              className="bg-white text-[hsl(var(--brand-primary))] text-sm px-4 py-1.5 rounded-sm hover:bg-[hsl(var(--bg-subtle))] disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />
              Régénérer les images
            </button>
            <button
              onClick={deleteSelected}
              data-testid="bulk-delete-btn"
              className="bg-white/10 border border-white text-white text-sm px-4 py-1.5 rounded-sm hover:bg-white hover:text-[hsl(var(--brand-primary))] flex items-center gap-2"
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              Supprimer la sélection
            </button>
            <button
              onClick={clearSelection}
              data-testid="bulk-clear-btn"
              className="text-sm underline opacity-80 hover:opacity-100"
            >
              Désélectionner
            </button>
          </div>
        </div>
      )}

      {/* TABLE */}
      <div className="bg-white border border-[hsl(var(--border-light))] rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-recipes-table">
          <thead className="bg-[hsl(var(--bg-subtle))]">
            <tr>
              <th className="px-3 py-3 w-10">
                <input
                  type="checkbox"
                  checked={items.length > 0 && selectedIds.size === items.length}
                  onChange={toggleAll}
                  data-testid="select-all-checkbox"
                  className="accent-[hsl(var(--brand-primary))]"
                  aria-label="Tout sélectionner"
                />
              </th>
              <th className="text-left px-4 py-3 font-medium">
                <button data-testid="sort-title-btn" onClick={() => toggleSort("title")} className="flex items-center gap-1 hover:text-[hsl(var(--brand-primary))]">
                  Titre{sortField === "title" ? (sortOrder === "asc" ? " ↑" : " ↓") : ""}
                </button>
              </th>
              <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Catégorie</th>
              <th className="text-left px-4 py-3 font-medium hidden md:table-cell">
                <button data-testid="sort-ig-btn" onClick={() => toggleSort("glycemic_index")} className="flex items-center gap-1 hover:text-[hsl(var(--brand-primary))]">
                  IG{sortField === "glycemic_index" ? (sortOrder === "asc" ? " ↑" : " ↓") : ""}
                </button>
              </th>
              <th className="text-left px-4 py-3 font-medium">Image IA</th>
              <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Source</th>
              <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">
                <button data-testid="sort-views-btn" onClick={() => toggleSort("views")} className="flex items-center gap-1 hover:text-[hsl(var(--brand-primary))]">
                  Vues{sortField === "views" ? (sortOrder === "asc" ? " ↑" : " ↓") : ""}
                </button>
              </th>
              <th className="text-left px-4 py-3 font-medium">État</th>
              <th className="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              if (loading) {
                return <tr><td colSpan={9} className="px-4 py-8 text-center text-[hsl(var(--text-muted))]">Chargement…</td></tr>;
              }
              if (items.length === 0) {
                return <tr><td colSpan={9} className="px-4 py-12 text-center text-[hsl(var(--text-muted))]">{activeFiltersCount > 0 ? "Aucune recette ne correspond aux filtres." : "Aucune recette. Générez-en une !"}</td></tr>;
              }
              return items.map((r) => (
              <tr
                key={r.id}
                className={`border-t border-[hsl(var(--border-light))] hover:bg-[hsl(var(--bg-subtle))]/50 ${selectedIds.has(r.id) ? "bg-[hsl(var(--brand-primary)/0.04)]" : ""}`}
                data-testid={`admin-recipe-row-${r.id}`}
              >
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(r.id)}
                    onChange={() => toggleOne(r.id)}
                    data-testid={`select-recipe-${r.id}`}
                    className="accent-[hsl(var(--brand-primary))]"
                    aria-label={`Sélectionner ${r.title}`}
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-[hsl(var(--text-primary))]">{r.title}</div>
                  <div className="text-xs text-[hsl(var(--text-muted))]">{r.slug}</div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">{r.category}</td>
                <td className="px-4 py-3 hidden md:table-cell">{r.glycemic_index} ({r.gi_level})</td>
                <td className="px-4 py-3" data-testid={`has-ai-image-${r.id}`}>
                  {r.has_ai_image ? (
                    <span className="text-xs px-2 py-1 rounded-sm bg-[hsl(var(--gi-low)/0.15)] text-[hsl(var(--gi-low))] inline-flex items-center gap-1">
                      <ImageIcon className="w-3 h-3" strokeWidth={1.5} /> Oui
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded-sm bg-orange-100 text-orange-800 inline-flex items-center gap-1">
                      <ImageIcon className="w-3 h-3" strokeWidth={1.5} /> Non
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">{r.auto_generated ? "IA" : "Manuel"}</td>
                <td className="px-4 py-3 hidden lg:table-cell">{r.views || 0}</td>
                <td className="px-4 py-3">
                  <button onClick={() => togglePublish(r)} data-testid={`toggle-publish-${r.id}`} className={`text-xs px-2 py-1 rounded-sm ${r.published ? "bg-[hsl(var(--gi-low)/0.15)] text-[hsl(var(--gi-low))]" : "bg-gray-200 text-gray-600"}`}>
                    {r.published ? "Publié" : "Brouillon"}
                  </button>
                </td>
                <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                  <button
                    onClick={() => setAffEditing({ recipe: r, products: r.affiliate_products?.length ? r.affiliate_products.map((p) => ({ ...p })) : [{ name: "", url: "", provider: "Amazon" }] })}
                    data-testid={`affiliate-recipe-${r.id}`}
                    title="Produits affiliés"
                    className="inline-flex items-center text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--brand-primary))]"
                  >
                    <ShoppingBag className="w-4 h-4" strokeWidth={1.5} />
                    {r.affiliate_products?.length > 0 && (
                      <span className="ml-0.5 text-xs text-[hsl(var(--brand-primary))]">{r.affiliate_products.length}</span>
                    )}
                  </button>
                  <Link to={`/recettes/${r.slug}`} target="_blank" data-testid={`view-recipe-${r.id}`} className="inline-flex items-center text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--brand-primary))]">
                    <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                  </Link>
                  <Link
                    to={`/admin/recipes/${r.id}/edit`}
                    data-testid={`edit-recipe-${r.id}`}
                    title="Éditer la recette"
                    className="inline-flex items-center text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--brand-primary))]"
                  >
                    <Pencil className="w-4 h-4" strokeWidth={1.5} />
                  </Link>
                  <button
                    onClick={() => regenerateOne(r.id, r.title)}
                    disabled={!!singleRegen[r.id]}
                    data-testid={`regen-image-${r.id}`}
                    title="Régénérer l'image Nano Banana"
                    className="inline-flex items-center text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--brand-primary))] disabled:opacity-40"
                  >
                    <RefreshCw className={`w-4 h-4 ${singleRegen[r.id] ? "animate-spin" : ""}`} strokeWidth={1.5} />
                  </button>
                  <button onClick={() => remove(r.id)} data-testid={`delete-recipe-${r.id}`} className="text-[hsl(var(--brand-primary))] hover:text-[hsl(var(--brand-primary-hover))]">
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
              ));
            })()}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-4 text-sm" data-testid="pagination-bar">
        <div className="flex items-center gap-3">
          <label className="text-xs text-[hsl(var(--text-muted))]">Par page</label>
          <select
            data-testid="page-size-select"
            value={pageSize}
            onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
            className="bg-white border border-[hsl(var(--border-light))] rounded-sm px-2 py-1 focus:outline-none focus:border-[hsl(var(--brand-primary))]"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className="text-[hsl(var(--text-secondary))]">
            {total === 0 ? "0" : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)}`} sur {total}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="page-prev-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            className="px-3 py-1 border border-[hsl(var(--border-light))] rounded-sm hover:border-[hsl(var(--brand-primary))] disabled:opacity-40"
          >
            ← Précédent
          </button>
          <span data-testid="page-indicator" className="text-[hsl(var(--text-secondary))]">
            Page {page} / {Math.max(1, Math.ceil(total / pageSize))}
          </span>
          <button
            data-testid="page-next-btn"
            onClick={() => setPage((p) => (p * pageSize < total ? p + 1 : p))}
            disabled={page * pageSize >= total || loading}
            className="px-3 py-1 border border-[hsl(var(--border-light))] rounded-sm hover:border-[hsl(var(--brand-primary))] disabled:opacity-40"
          >
            Suivant →
          </button>
        </div>
      </div>

      {/* AFFILIATE PRODUCTS MODAL */}
      {affEditing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setAffEditing(null)} data-testid="affiliate-modal">
          <div className="bg-white rounded-sm max-w-2xl w-full my-12 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-[hsl(var(--border-light))] flex items-start justify-between">
              <div>
                <p className="overline mb-1">Produits affiliés</p>
                <h2 className="font-serif text-2xl">{affEditing.recipe.title}</h2>
                <p className="text-xs text-[hsl(var(--text-muted))] mt-1">2-3 produits Amazon / Awin liés à la recette</p>
              </div>
              <button onClick={() => setAffEditing(null)} data-testid="affiliate-close" className="text-[hsl(var(--text-muted))]"><X className="w-5 h-5" strokeWidth={1.5} /></button>
            </div>

            <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
              {affEditing.products.map((p, idx) => (
                <div key={idx} className="border border-[hsl(var(--border-light))] rounded-sm p-4 relative" data-testid={`affiliate-row-${idx}`}>
                  <button
                    onClick={() => setAffEditing({ ...affEditing, products: affEditing.products.filter((_, i) => i !== idx) })}
                    data-testid={`affiliate-remove-${idx}`}
                    className="absolute top-2 right-2 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--brand-primary))]"
                  >
                    <X className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-[hsl(var(--text-muted))]">Nom du produit</label>
                      <input
                        type="text"
                        value={p.name}
                        onChange={(e) => {
                          const next = [...affEditing.products];
                          next[idx] = { ...p, name: e.target.value };
                          setAffEditing({ ...affEditing, products: next });
                        }}
                        data-testid={`affiliate-name-${idx}`}
                        className="w-full mt-1 px-3 py-2 text-sm border border-[hsl(var(--border-light))] rounded-sm focus:outline-none focus:border-[hsl(var(--brand-primary))]"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-[hsl(var(--text-muted))]">Source</label>
                      <select
                        value={p.provider || "Amazon"}
                        onChange={(e) => {
                          const next = [...affEditing.products];
                          next[idx] = { ...p, provider: e.target.value };
                          setAffEditing({ ...affEditing, products: next });
                        }}
                        className="w-full mt-1 px-3 py-2 text-sm border border-[hsl(var(--border-light))] rounded-sm focus:outline-none focus:border-[hsl(var(--brand-primary))]"
                      >
                        <option value="Amazon">Amazon</option>
                        <option value="Awin">Awin</option>
                        <option value="Cdiscount">Cdiscount</option>
                        <option value="Greenweez">Greenweez</option>
                        <option value="Autre">Autre</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs uppercase tracking-wider text-[hsl(var(--text-muted))]">Lien affilié (URL)</label>
                      <input
                        type="url"
                        value={p.url}
                        onChange={(e) => {
                          const next = [...affEditing.products];
                          next[idx] = { ...p, url: e.target.value };
                          setAffEditing({ ...affEditing, products: next });
                        }}
                        data-testid={`affiliate-url-${idx}`}
                        placeholder="https://www.amazon.fr/dp/XXXXXX?tag=..."
                        className="w-full mt-1 px-3 py-2 text-sm font-mono border border-[hsl(var(--border-light))] rounded-sm focus:outline-none focus:border-[hsl(var(--brand-primary))]"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs uppercase tracking-wider text-[hsl(var(--text-muted))]">Image (URL, optionnel)</label>
                      <input
                        type="url"
                        value={p.image_url || ""}
                        onChange={(e) => {
                          const next = [...affEditing.products];
                          next[idx] = { ...p, image_url: e.target.value };
                          setAffEditing({ ...affEditing, products: next });
                        }}
                        className="w-full mt-1 px-3 py-2 text-sm border border-[hsl(var(--border-light))] rounded-sm focus:outline-none focus:border-[hsl(var(--brand-primary))]"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-xs uppercase tracking-wider text-[hsl(var(--text-muted))]">Description courte (optionnel)</label>
                      <input
                        type="text"
                        value={p.description || ""}
                        onChange={(e) => {
                          const next = [...affEditing.products];
                          next[idx] = { ...p, description: e.target.value };
                          setAffEditing({ ...affEditing, products: next });
                        }}
                        className="w-full mt-1 px-3 py-2 text-sm border border-[hsl(var(--border-light))] rounded-sm focus:outline-none focus:border-[hsl(var(--brand-primary))]"
                      />
                    </div>
                  </div>
                </div>
              ))}

              {affEditing.products.length < 5 && (
                <button
                  onClick={() => setAffEditing({ ...affEditing, products: [...affEditing.products, { name: "", url: "", provider: "Amazon" }] })}
                  data-testid="affiliate-add"
                  className="w-full border border-dashed border-[hsl(var(--border-default))] py-3 text-sm text-[hsl(var(--text-secondary))] hover:border-[hsl(var(--brand-primary))] hover:text-[hsl(var(--brand-primary))] transition-colors rounded-sm flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" strokeWidth={1.5} /> Ajouter un produit
                </button>
              )}
            </div>

            <div className="p-6 border-t border-[hsl(var(--border-light))] flex justify-end gap-3">
              <button onClick={() => setAffEditing(null)} className="btn-outline">Annuler</button>
              <button
                onClick={async () => {
                  setAffSaving(true);
                  try {
                    const cleaned = affEditing.products.filter((p) => p.name && p.url);
                    await api.patch(`/admin/recipes/${affEditing.recipe.id}`, { affiliate_products: cleaned });
                    toast.success(`${cleaned.length} produit(s) enregistré(s)`);
                    setAffEditing(null);
                    load();
                  } catch (e) {
                    toast.error("Erreur de sauvegarde");
                  } finally {
                    setAffSaving(false);
                  }
                }}
                disabled={affSaving}
                data-testid="affiliate-save"
                className="btn-primary disabled:opacity-50"
              >
                <Save className="w-4 h-4" strokeWidth={1.5} />
                {affSaving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
